import {
    X,
    CalendarClock,
    Layers,
} from "lucide-react";
import React from "react";
import QuantityStepper from "./QuantityStepper";
import CommonButton from "../../CommonButton";

export type CartItem = {
    id: string;
    productId: number;
    title: string;
    price: string;
    image: string;
    quantity: number;

    isPet?: boolean;
    isMandatory?: boolean;

    // ============================================================
    // PET PURCHASE DETAILS
    // ============================================================

    purchaseType?: number | string;
    totalAmount?: number;
    paidAmount?: number;
    remainingAmount?: number;

    // ============================================================
    // SUBSCRIPTION DETAILS
    // ============================================================

    isSubscription?: boolean;

    billingCycle?: string | null;

    interval?: number | null;

    subscriptionDiscountPercent?: number;

    subscriptionDiscountAmount?: number;

    subscriptionPrice?: number;

    recurringPrice?: number;

    recurringDiscountPercent?: number;

    variantName?: string;

    // Original product pricing
    unitPrice?: number;

    unitSalePrice?: number;
};

export type CartBundleItem = {
    cartItemId: number;
    productVariantId: number;
    productName: string;
    variantName: string;
    imageUrl: string;
    quantity: number;
    unitPrice: number;
    total: number;
};

export type CartBundle = {
    bundleId: number;
    name: string;
    imageUrl: string;
    bundlePrice: number;
    originalPrice: number;
    savingAmount: number;
    items: CartBundleItem[];
    total: number;
};

type CartDrawerProps = {
    cart: CartItem[];

    bundles?: CartBundle[];

    isOpen: boolean;

    onClose: () => void;

    onIncrement: (id: string) => void;

    onDecrement: (id: string) => void;

    onRemove: (id: string) => void;

    onRemoveBundle?: (bundleId: number) => void;

    onAddToCart?: (product: any) => void;

    handleCheckoutClick: () => void;
};

// ============================================================
// PET PURCHASE TYPE LABEL
// ============================================================

const getPetPurchaseTypeLabel = (
    purchaseType: number | string | undefined
): string => {
    if (
        purchaseType === null ||
        purchaseType === undefined ||
        purchaseType === ""
    ) {
        return "Pet Reservation";
    }

    const value = String(purchaseType)
        .trim()
        .toLowerCase()
        .replace(/[_-]/g, " ")
        .replace(/\s+/g, " ");

    // Numeric purchase types
    if (value === "1") {
        return "Full Payment";
    }

    if (value === "2") {
        return "Partial Booking";
    }

    // String purchase types
    if (
        value === "partialbooking" ||
        value === "partial booking" ||
        value === "partial"
    ) {
        return "Partial Booking";
    }

    if (
        value === "fullpayment" ||
        value === "full payment" ||
        value === "full"
    ) {
        return "Full Payment";
    }

    if (
        value.includes("partial") ||
        value.includes("booking")
    ) {
        return "Partial Booking";
    }

    if (value.includes("full")) {
        return "Full Payment";
    }

    return String(purchaseType);
};

// ============================================================
// FORMAT SUBSCRIPTION BILLING
// ============================================================

const getBillingLabel = (
    interval?: number | null,
    billingCycle?: string | null
): string => {
    const cycle = billingCycle || "Month";
    const count = Number(interval || 1);

    if (count === 1) {
        return `Every 1 ${cycle}`;
    }

    const normalizedCycle =
        cycle.toLowerCase().endsWith("s")
            ? cycle
            : `${cycle}s`;

    return `Every ${count} ${normalizedCycle}`;
};

// ============================================================
// CART DRAWER
// ============================================================

const CartDrawer: React.FC<CartDrawerProps> = ({
    cart,
    bundles = [],
    isOpen,
    onClose,
    onDecrement,
    onIncrement,
    onRemove,
    onRemoveBundle,
    handleCheckoutClick,
}) => {

    // ========================================================
    // ITEM TOTAL
    //
    // IMPORTANT:
    // price already represents the amount for that cart line.
    // Do NOT multiply it by quantity here.
    //
    // Example:
    // Product price = AED 116.60
    // Quantity = 2
    //
    // Cart API may already return line total.
    // ========================================================

    const itemsTotal = cart.reduce((acc, item) => {
        const price = Number(
            String(item.price || "")
                .replace(/[^0-9.-]/g, "")
        ) || 0;

        return acc + price;
    }, 0);

    // ========================================================
    // BUNDLE TOTAL
    // ========================================================

    const bundlesTotal = bundles.reduce(
        (acc, bundle) =>
            acc + Number(bundle.bundlePrice || 0),
        0
    );

    // ========================================================
    // GRAND TOTAL
    // ========================================================

    const total =
        itemsTotal +
        bundlesTotal;

    // ========================================================
    // EMPTY
    // ========================================================

    const isEmpty =
        cart.length === 0 &&
        bundles.length === 0;

    return (
        <>
            {/* ==================================================
                OVERLAY
            ================================================== */}

            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/40 z-[9998]"
                    onClick={onClose}
                />
            )}

            {/* ==================================================
                DRAWER
            ================================================== */}

            <div
                className={`
                    fixed
                    top-0
                    right-0
                    h-full
                    w-full
                    md:w-[400px]
                    bg-white
                    z-[9999]
                    shadow-lg
                    transform
                    transition-transform
                    duration-300
                    flex
                    flex-col
                    text-left
                    ${
                        isOpen
                            ? "translate-x-0"
                            : "translate-x-full"
                    }
                `}
            >

                {/* ==================================================
                    HEADER
                    KEEPING ORIGINAL UI
                ================================================== */}

                <div className="p-4 border-b flex items-center justify-between shrink-0">

                    <span className="font-bold text-lg text-logoHeading">
                        Your Cart
                    </span>

                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-gray-100 transition"
                    >
                        <X
                            size={20}
                            className="text-iconColor"
                        />
                    </button>

                </div>

                {/* ==================================================
                    CART ITEMS
                ================================================== */}

                <div className="p-4 space-y-5 overflow-y-auto flex-1">

                    {isEmpty ? (

                        <p className="text-center text-gray-500 py-8">
                            Your cart is empty
                        </p>

                    ) : (

                        <>

                            {/* ==================================================
                                NORMAL PRODUCTS + PETS
                            ================================================== */}

                            {cart.map((item) => (

                                <div
                                    key={item.id}
                                    className="
                                        flex
                                        gap-3
                                        border-b
                                        border-slate-200
                                        pb-4
                                        last:border-none
                                    "
                                >

                                    {/* ==================================================
                                        IMAGE
                                    ================================================== */}

                                    <img
                                        src={item.image}
                                        className="
                                            w-16
                                            h-16
                                            object-cover
                                            rounded
                                            shrink-0
                                            bg-slate-50
                                        "
                                        alt={item.title}
                                    />

                                    {/* ==================================================
                                        CONTENT
                                    ================================================== */}

                                    <div className="flex-1 min-w-0">

                                        {/* ==================================================
                                            TITLE
                                        ================================================== */}

                                        <p
                                            className="
                                                text-sm
                                                font-medium
                                                text-logoHeading
                                                truncate
                                            "
                                            title={item.title}
                                        >
                                            {item.title}
                                        </p>

                                        {/* ==================================================
                                            VARIANT
                                        ================================================== */}

                                        {item.variantName && (
                                            <p className="
                                                text-xs
                                                text-gray-500
                                                mt-0.5
                                            ">
                                                {item.variantName}
                                            </p>
                                        )}

                                        {/* ==================================================
                                            PRICE
                                        ================================================== */}

                                        <div className="flex items-baseline gap-2">

                                            <p className="
                                                text-sm
                                                font-black
                                                text-mainBrandColor
                                            ">
                                                {item.price}
                                            </p>

                                            {/* ==================================================
                                                SUBSCRIPTION DISCOUNT
                                            ================================================== */}

                                            {item.isSubscription &&
                                                item.subscriptionDiscountPercent &&
                                                item.subscriptionDiscountPercent > 0 && (

                                                    <span className="
                                                        text-[10px]
                                                        font-bold
                                                        text-green-600
                                                        bg-green-50
                                                        px-1.5
                                                        py-0.5
                                                        rounded
                                                    ">
                                                        Save{" "}
                                                        {
                                                            item.subscriptionDiscountPercent
                                                        }
                                                        %
                                                    </span>

                                                )}

                                        </div>

                                        {/* ==================================================
                                            SUBSCRIPTION DETAILS
                                            ONLY SHOWN IF SUBSCRIPTION EXISTS
                                        ================================================== */}

                                  {item.isSubscription && (
    <div className="
        flex
        items-center
        gap-1.5
        mt-2
        text-[11px]
        font-medium
        text-orange-700
        bg-orange-50
        border
        border-orange-100
        px-2
        py-1
        rounded
        w-fit
    ">
        <CalendarClock
            size={12}
            className="text-orange-500"
        />

        <span>
            Autoship:{" "}
            {item.billingCycle || "Monthly"}
        </span>
    </div>
)}

                                        {/* ==================================================
                                            PET / PRODUCT ACTION AREA
                                        ================================================== */}

                                        <div className="
                                            flex
                                            items-center
                                            justify-between
                                            gap-3
                                            mt-2
                                        ">

                                            {/* ==================================================
                                                PET
                                            ================================================== */}

                                            {item.isPet ? (

                                                <div className="flex flex-col gap-1">

                                                    <span
                                                        className={`
                                                            inline-flex
                                                            items-center
                                                            w-fit
                                                            text-[11px]
                                                            font-semibold
                                                            px-2.5
                                                            py-1
                                                            rounded-md
                                                            ${
                                                                String(
                                                                    item.purchaseType ??
                                                                        ""
                                                                )
                                                                    .toLowerCase()
                                                                    .includes(
                                                                        "partial"
                                                                    ) ||
                                                                String(
                                                                    item.purchaseType ??
                                                                        ""
                                                                ) === "2"
                                                                    ? "text-blue-700 bg-blue-50"
                                                                    : "text-logoHeading bg-accent/30"
                                                            }
                                                        `}
                                                    >
                                                        {getPetPurchaseTypeLabel(
                                                            item.purchaseType
                                                        )}
                                                    </span>

                                                </div>

                                            ) : (

                                                /* ==================================================
                                                    NORMAL PRODUCT QUANTITY
                                                ================================================== */

                                                <QuantityStepper
                                                    qty={item.quantity}
                                                    onIncrement={() =>
                                                        onIncrement(item.id)
                                                    }
                                                    onDecrement={() =>
                                                        onDecrement(item.id)
                                                    }
                                                    className="
                                                        flex-1
                                                        max-w-[150px]
                                                        h-[36px]
                                                    "
                                                />

                                            )}

                                            {/* ==================================================
                                                REMOVE
                                            ================================================== */}

                                            {!item.isMandatory && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        onRemove(item.id)
                                                    }
                                                    className="
                                                        text-red-500
                                                        text-sm
                                                        whitespace-nowrap
                                                        shrink-0
                                                        hover:underline
                                                    "
                                                >
                                                    Remove
                                                </button>
                                            )}

                                        </div>

                                    </div>

                                </div>

                            ))}

                            {/* ==================================================
                                BUNDLES
                                EXISTING UI
                            ================================================== */}

                            {bundles.map((bundle) => (

                                <div
                                    key={bundle.bundleId}
                                    className="
                                        border
                                        border-gray-200
                                        rounded-lg
                                        p-3
                                        bg-gray-50/50
                                        space-y-3
                                    "
                                >

                                    {/* BUNDLE HEADER */}

                                    <div className="
                                        flex
                                        gap-3
                                        border-b
                                        border-gray-100
                                        pb-2
                                    ">

                                        <img
                                            src={bundle.imageUrl}
                                            className="
                                                w-14
                                                h-14
                                                object-cover
                                                rounded
                                                shrink-0
                                                border
                                                border-gray-200
                                            "
                                            alt={bundle.name}
                                        />

                                        <div className="
                                            flex-1
                                            min-w-0
                                        ">

                                            <div className="
                                                flex
                                                items-center
                                                gap-1
                                                text-[11px]
                                                font-bold
                                                uppercase
                                                tracking-wider
                                                text-purple-600
                                                bg-purple-50
                                                px-2
                                                py-0.5
                                                rounded
                                                w-fit
                                                mb-0.5
                                            ">

                                                <Layers size={10} />

                                                <span>
                                                    Bundle Package
                                                </span>

                                            </div>

                                            <p className="
                                                text-sm
                                                font-bold
                                                text-logoHeading
                                                truncate
                                            ">
                                                {bundle.name}
                                            </p>

                                            <p className="
                                                text-sm
                                                font-black
                                                text-mainBrandColor
                                            ">
                                                AED{" "}
                                                {Number(
                                                    bundle.bundlePrice
                                                ).toFixed(2)}
                                            </p>

                                        </div>

                                        {onRemoveBundle && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onRemoveBundle(
                                                        bundle.bundleId
                                                    )
                                                }
                                                className="
                                                    text-gray-400
                                                    hover:text-red-500
                                                    self-start
                                                    p-1
                                                    transition
                                                "
                                            >
                                                <X size={16} />
                                            </button>
                                        )}

                                    </div>

                                    {/* BUNDLE ITEMS */}

                                    <div className="
                                        space-y-2
                                        pl-2
                                        border-l-2
                                        border-gray-200
                                    ">

                                        {bundle.items.map((bItem) => (

                                            <div
                                                key={bItem.cartItemId}
                                                className="
                                                    text-xs
                                                    flex
                                                    justify-between
                                                    items-start
                                                    gap-2
                                                    text-gray-600
                                                "
                                            >

                                                <div className="
                                                    min-w-0
                                                    flex-1
                                                ">

                                                    <p className="
                                                        font-medium
                                                        truncate
                                                        text-gray-800
                                                    ">
                                                        {bItem.productName}
                                                    </p>

                                                    <p className="
                                                        text-[10px]
                                                        text-gray-400
                                                    ">
                                                        {bItem.variantName}
                                                    </p>

                                                </div>

                                                <span className="
                                                    shrink-0
                                                    font-semibold
                                                    text-gray-500
                                                ">
                                                    x{bItem.quantity}
                                                </span>

                                            </div>

                                        ))}

                                    </div>

                                </div>

                            ))}

                        </>

                    )}

                </div>

                {/* ==================================================
                    FOOTER
                    KEEPING ORIGINAL UI
                ================================================== */}

                <div className="
                    p-4
                    border-t
                    bg-white
                    shrink-0
                ">

                    <p className="
                        mb-2
                        font-bold
                        text-red-800
                        text-lg
                    ">
                        Total: AED{" "}
                        {total.toFixed(2)}
                    </p>

                    <CommonButton
                        text="Checkout"
                        className="
                            w-full
                            bg-mainBrandColor
                            text-white
                            font-bold
                            h-12
                        "
                        rounded="md"
                        variant="filled"
                        onClick={handleCheckoutClick}
                    />

                </div>

            </div>
        </>
    );
};

export default CartDrawer;